import React from 'react';
import { Bluetooth, CreditCard, ShieldCheck, UserCog } from 'lucide-react';
import { Badge } from '../ui/badge';
import {
  OCCUPANCY_BADGE_CLS,
  isPersonOccupant,
  bindingSourceKind,
  bindingSourceLabel,
  isActingDriverStaff,
} from '../../utils/occupancy';

// A single bound-entity pill rendered identically on the Live Tracking TelemetryPanel and the
// Fleet card. Layout: [source icon] Name [driver-eligible shield]. People deep-link to People
// (clickable button); assets render as a static Badge. `last` dims the pill for previous-trip
// occupants so "Last:" trailers/equipment are styled consistently (was previously plain text).
// `stale` dims a CURRENT pill whose freshness can't be confirmed (offline device) — `staleNote`
// explains why in the tooltip.
export const OccupancyPill = ({ rowType, name, personType, source, last = false, stale = false, staleNote, onClick, testId }) => {
  const kind = bindingSourceKind(source);
  const SourceIcon = kind === 'ble' ? Bluetooth : kind === 'rfid' ? CreditCard : kind === 'manual' ? UserCog : null;
  const actingDriver = isActingDriverStaff(rowType, personType);
  const cls = OCCUPANCY_BADGE_CLS[rowType] || '';
  const dim = (last || stale) ? 'opacity-60 hover:opacity-100' : 'hover:opacity-80';
  const base = `inline-flex items-center gap-1 max-w-[170px] rounded px-1.5 py-0.5 text-[10px] font-medium border-0 transition-opacity ${cls} ${dim}`;
  const srcTitle = bindingSourceLabel(kind);

  const inner = (
    <>
      {SourceIcon && (
        <SourceIcon
          className="w-2.5 h-2.5 shrink-0 opacity-90"
          aria-hidden="true"
          data-testid={testId ? `${testId}-source-${kind}` : undefined}
        />
      )}
      <span className="truncate">{name}</span>
      {actingDriver && (
        <ShieldCheck
          className="w-2.5 h-2.5 shrink-0"
          aria-hidden="true"
          data-testid={testId ? `${testId}-driver-eligible` : undefined}
        />
      )}
    </>
  );

  if (isPersonOccupant(rowType) && onClick) {
    const title = actingDriver
      ? `${name} — driver-eligible Staff acting as driver${srcTitle ? ` · ${srcTitle}` : ''} (open in People)`
      : `Open ${name} in People${srcTitle ? ` · ${srcTitle}` : ''}`;
    return (
      <button type="button" onClick={onClick} className={base} data-testid={testId} title={staleNote ? `${title} · ${staleNote}` : title}>
        {inner}
      </button>
    );
  }

  return (
    <Badge className={base} data-testid={testId} title={staleNote ? `${staleNote}${srcTitle ? ` · ${srcTitle}` : ''}` : (srcTitle || name)}>
      {inner}
    </Badge>
  );
};
